/* eslint-disable react/no-did-update-set-state */
/* eslint-disable no-unused-expressions */
/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import React from 'react';
import { IMAGE_CDN_URL } from '../../../utilities/imageCDN';
import { throttle, flatten } from '../../../utilities/helperFunctions';

import './ResponsivePicture.scss';

/*

    NOTE: This will attempt to deliver hi-res versions of your image to devices with hi-res screens, so make sure you upload
    an original file at least 2x the image's expected desktop size if possible.

    Also, unless the image provided to it is smaller than its container OR a format other than jpg is specified in the transforms,
    this component will UPSCALE the requested dimensions by 30 - 50% and let the browser scale it back down at render time.
    This is in response to client feedback and is designed to increase the quality of the image by reducing the impact of jpg compression artifacts.

    When we're dealing with a hard-coded image that is a non-jpg, please specify its format in the transforms. This will ensure
    our download for that image is not upscaled, so it's more efficient. Currently there is no way in the cloudinary API to
    automatically account for format in user uploads.

 */

const TRANSFORM_PREFIXES = {
    quality: 'q',
    aspect_ratio: 'ar',
    gravity: 'g',
    crop: 'c',
    radius: 'r',
    flags: 'fl',
    background: 'b',
    format: 'f',
};

const TRANSFORMS_SHAPE = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
        // do not put width and height in transforms! We're going to take that from the breakpoint.width and breakpoint.height properties
        // Documentation on transform properties is at https://cloudinary.com/documentation/image_transformation_reference
        crop: PropTypes.oneOf([
            'scale',
            'fit',
            'limit',
            'mfit',
            'fill',
            'lfill',
            'pad',
            'lpad',
            'mpad',
        ]),
        aspect_ratio: PropTypes.string,
        quality: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        gravity: PropTypes.string,
        radius: PropTypes.string,
        flags: PropTypes.array,
        background: PropTypes.string,
    }),
]);

export const BREAKPOINT_SHAPE = PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number,
    transforms: TRANSFORMS_SHAPE,
});

const BREAKPOINT_STRINGS = {
    desktop: 'DESKTOP',
    tablet: 'TABLET',
    smallTablet: 'SMALL_TABLET',
    mobile: 'MOBILE',
};

const BREAKPOINT_CONSTANTS = {
    DESKTOP: {
        MINWIDTH: 992,
        MEDIA: '(min-width: 992px)',
        PRIORITY: 0,
    },
    TABLET: {
        MINWIDTH: 768,
        MEDIA: '(min-width: 768px)',
        PRIORITY: 1,
    },
    SMALL_TABLET: {
        MINWIDTH: 575,
        MEDIA: '(min-width: 575px)',
        PRIORITY: 2,
    },
    MOBILE: {
        MINWIDTH: 0,
        MEDIA: '(min-width: 0)',
        PRIORITY: 3,
    },
};

const JPGMIMETYPES = [
    ['webp', 'image/webp'],
    ['jxr', 'image/vnd.ms-photo'],
    ['jp2', 'image/jp2'],
    ['jpg', 'image/jpg'],
];

const MIMETYPEMAP = {
    jpg: JPGMIMETYPES,
    heic: JPGMIMETYPES,
    heif: JPGMIMETYPES,
    png: [
        ['webp', 'image/webp'],
        ['png', 'image/png'],
    ],
    gif: [
        ['webp', 'image/webp'],
        ['gif', 'image/gif'],
    ],
};

export default class ResponsivePicture extends React.PureComponent {
    checkImageLocation = throttle(async () => {
        const element = this.containerRef.current;
        const bounding = element?.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight;

        // This will trigger the load if the image is within --  or near -- the viewport
        if (
            bounding.top > 0 - viewportHeight / 2 &&
            bounding.bottom <= viewportHeight + viewportHeight / 4
        ) {
            this.loadPicture();
        }
    }, 333);

    constructor(props) {
        super();
        this.baseCls = 'component-Elements-Picture';

        const cloudinaryInfo = this.getCloudinaryInfo(props);

        this.state = {
            cloudinaryID: cloudinaryInfo.id,
            format: cloudinaryInfo.format,
            isAnimatedGif: cloudinaryInfo.isAnimatedGif,
            isLazyLoad: props.lazyLoad,
            isLoading: props.useShine || props.lazyLoad,
            prevBreakpoint: null,
            containerStyle: {},
            inlineStyle: {},
            width: 0,
            height: 0,
            isSmallImage: false,
        };

        this.containerRef = React.createRef();
        this.imgRef = React.createRef();
        this.vidRef = React.createRef();
        this.initTime = '';
    }

    componentDidMount = () => {
        this.props.forceImageSize && this.setImageSize();
        window.addEventListener('resize', this.setImageSize);
        if (
            (this.imgRef.current && this.imgRef.current.complete) ||
            (this.vidRef.current && this.vidRef.current.readyState === 4)
        ) {
            // Because of SSR, there's a chance the browser will finish downloading the picture before the onLoad event is bound.
            // This should catch that condition.
            this.handleOnLoad();
        }

        if (this.props.lazyLoad && !this.props.waitForLoadProp) {
            window.addEventListener('scroll', this.checkImageLocation);
            this.checkImageLocation();
        }
    };

    componentWillUnmount = () => {
        window.removeEventListener('resize', this.setImageSize);
        if (this.props.lazyLoad) {
            window.addEventListener('scroll', this.checkImageLocation);
            this.checkImageLocation();
        }
    };

    componentDidUpdate = (prevProps) => {
        if (prevProps.lazyLoad && !this.props.lazyLoad) {
            // if the lazyLoad prop changes externally, load the picture now!
            this.loadPicture();
        }

        if (prevProps.cloudinaryID !== this.props.cloudinaryID) {
            const cloudinaryInfo = this.getCloudinaryInfo();
            this.setState({
                cloudinaryID: cloudinaryInfo.id,
                format: cloudinaryInfo.format,
                isAnimatedGif: cloudinaryInfo.isAnimatedGif,
            });
        }
    };

    getCloudinaryInfo = (props = false) => {
        const cloudinaryIdArr =
            (props && props.cloudinaryID && props.cloudinaryID.split('.')) ||
            (this.props &&
                this.props.cloudinaryID &&
                this.props.cloudinaryID.split('.')) ||
            [];
        const output = {
            id: cloudinaryIdArr[0],
            format: cloudinaryIdArr.length > 1 ? cloudinaryIdArr.pop() : false,
            isAnimatedGif:
                cloudinaryIdArr.length > 1 && cloudinaryIdArr.pop() === 'anim',
        };

        output.filename = output.format
            ? `${output.id}.${output.format}`
            : output.id;

        return output;
    };

    buildSources = (breakpoint, i) => {
        switch (this.state.format) {
            case 'gif': // NOTE: This is a NON-animated gif. Animated gifs are detected at time of upload and displayed (as transformed to video by Cloudinary) by this.buildVideoSources
            case 'png':
            case 'jpg':
            case 'heif':
            case 'heic': {
                const mimeTypes = MIMETYPEMAP[this.state.format];
                // This returns an array of source elements with mimetypes
                return mimeTypes.map((mime, h) => {
                    const sourceProps = {
                        media:
                            BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]]
                                .MEDIA,
                        priority: `${
                            BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]]
                                .PRIORITY
                        }.${h}`,
                        srcSet: this.buildSrcSet(
                            this.props.breakpoints[breakpoint],
                            mime[0]
                        ),
                        key: `${this.state.cloudinaryID}-${this.initTime}-${i}-${h}`,
                        type: mime[1],
                    };

                    return <source {...sourceProps} />;
                });
            }

            default: {
                const sourceProps = {
                    media:
                        BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]]
                            .MEDIA,
                    key: `${this.state.cloudinaryID}-${this.initTime}-${i}`,
                    priority:
                        BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]]
                            .PRIORITY,
                    srcSet: this.buildSrcSet(
                        this.props.breakpoints[breakpoint]
                    ),
                };

                return <source {...sourceProps} />;
            }
        }
    };

    buildVideoSources = (breakpoint, i) => {
        const mimeTypes = [
            ['webm', 'video/webm'],
            ['mp4', 'video/mp4'],
        ];

        const transforms = this.buildTransformsString(
            this.props.breakpoints[breakpoint]
        );

        return mimeTypes.map((mime, h) => {
            let id =
                (typeof this.state.cloudinaryID === 'string' &&
                    this.state.cloudinaryID.replace(/\/?v[0-9]*\//, '/')) ||
                '';
            id = id[0] !== '/' ? `/${id}` : id;
            const sourceProps = {
                media:
                    BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]].MEDIA,
                priority: `${
                    BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS[breakpoint]]
                        .PRIORITY
                }.${h}`,
                src: `${IMAGE_CDN_URL}/${
                    transforms ? `,${transforms}` : ''
                }/v1${id}.${mime[0]}`,
                key: `${this.state.cloudinaryID}-${this.initTime}-${i}-${h}`,
                type: mime[1],
            };
            return <source {...sourceProps} />;
        });
    };

    buildSrcSet = (breakpoint, extension = false) => {
        const transforms = this.buildTransformsString(breakpoint);
        const output = [];
        const maxDensity = this.state.isSmallImage ? 1 : this.props.maxDensity;
        for (let i = 1; i <= maxDensity; i += 1) {
            const fileFormat =
                extension ||
                (breakpoint.transforms && breakpoint.transforms.format);
            const dec =
                (!this.state.isSmallImage &&
                    i <= 2 &&
                    !this.isSpecifiedNonJpg(fileFormat) &&
                    ((i === 1 && 5) || 3)) ||
                0;
            const src = this.buildSrc(transforms, `${i}.${dec}`, extension);
            output.push(`${src} ${i}x`);
        }

        return output.join(', ');
    };

    buildSrc = (transforms = null, pixelDensity = '1.0', ext = false) => {
        // the ID is marked as a required string, but even if that's ignored for some reason we still don't want it to break the page

        let id =
            (typeof this.state.cloudinaryID === 'string' &&
                this.state.cloudinaryID.replace(/\/?v[0-9]*\//, '/')) ||
            '';
        id = id[0] !== '/' ? `/${id}` : id;
        const extension = ext || this.state.format;
        return `${IMAGE_CDN_URL}/dpr_${pixelDensity}${
            transforms ? `,${transforms}` : ''
        }/v1${id}${(extension && `.${extension}`) || ''}`;
    };

    buildFallbackSrc = () => {
        const {
            breakpoints: { desktop, tablet, smallTablet, mobile },
        } = this.props;

        const transforms =
            (desktop && this.buildTransformsString(desktop)) ||
            (tablet && this.buildTransformsString(tablet)) ||
            (smallTablet && this.buildTransformsString(smallTablet)) ||
            (mobile && this.buildTransformsString(mobile)) ||
            'w_500';
        return this.buildSrc(
            transforms,
            (!this.isSpecifiedNonJpg(transforms) && 1.5) || 1.0
        );
    };

    isSpecifiedNonJpg = (fileFormat = false) =>
        (this.state.format && this.state.format !== 'jpg') ||
        (fileFormat && fileFormat !== 'jpg');

    buildTransformsString = ({ transforms = null, width, height }) => {
        if (typeof transforms === 'string') {
            let outputTransforms = transforms;
            outputTransforms =
                width && !this.state.isSmallImage
                    ? (outputTransforms && `${outputTransforms},w_${width}`) ||
                      `w_${width}`
                    : outputTransforms;
            outputTransforms =
                height && !this.state.isSmallImage
                    ? (outputTransforms && `${outputTransforms},h_${height}`) ||
                      `h_${height}`
                    : outputTransforms;
            return outputTransforms;
        }

        let args = [];

        if (transforms) {
            const keys = Object.keys(transforms);
            args = keys.map((value) => {
                return `${TRANSFORM_PREFIXES[value]}_${transforms[value]}`;
            });
            if (keys.indexOf('quality') === -1) {
                args.push('q_auto');
            }
        } else {
            args.push('q_auto');
        }

        if (!this.state.isSmallImage) {
            width && args.push(`w_${width}`);
            height && args.push(`h_${height}`);
        }

        return args.join(',');
    };

    // To ensure we get the breakpoint we want, we need to order the sources correctly, desktop first, descending
    prioritizeSources = (a, b) => a.props.priority - b.props.priority;

    getCurrentBreakpoint = () => {
        const docWidth = document.documentElement.clientWidth;
        switch (true) {
            case docWidth >=
                BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS.desktop].MINWIDTH:
                return 'desktop';
            case docWidth >=
                BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS.tablet].MINWIDTH:
                return 'tablet';
            case docWidth >=
                BREAKPOINT_CONSTANTS[BREAKPOINT_STRINGS.smallTablet].MINWIDTH:
                return 'smallTablet';
            default:
                return 'mobile';
        }
    };

    setImageSize = (e) => {
        const container = this.containerRef.current;
        const img = this.imgRef.current;
        const video = this.vidRef.current;
        const eventType = (e && e.type) || '';

        const currentBreakpoint = this.getCurrentBreakpoint();
        if (
            (this.state.prevBreakpoint === currentBreakpoint &&
                eventType !== 'load') ||
            (img === null && video === null)
        ) {
            // no changes
            return;
        }

        if (
            (eventType === 'resize' && img && !img.complete) ||
            (video && video.readyState !== 4)
        ) {
            this.setState({
                isLoading: this.props.useShine,
                prevBreakpoint: currentBreakpoint,
                containerStyle: {},
                inlineStyle: {},
                width: 0,
                height: 0,
            });
            return;
        }

        const width = (img && img.naturalWidth) || (video && video.videoWidth);
        const height =
            (img && img.naturalHeight) || (video && video.videoHeight);

        const maxHeight = this.props.maxHeights[currentBreakpoint];

        // If we have an image that's naturally wider than its container, just display it. The browser will shrink it down.
        // Or, if we knew exactly what we wanted from the getgo, this is where the forceImageSize prop skips to.
        if (
            this.props.forceImageSize ||
            width >= container.clientWidth ||
            height >= maxHeight
        ) {
            const { isAnimatedGif } = this.state;
            if (isAnimatedGif) {
                // With video files we need to specify width, to be safe. Some browsers handle it better than others.
                this.setState({
                    prevBreakpoint: currentBreakpoint,
                    containerStyle: {},
                    inlineStyle: isAnimatedGif
                        ? { width: `${container.clientWidth}px` }
                        : {},
                });
                return;
            }
            // Check to see if we have a tall image that is taller than we would like. We resize it down to the specified max height and then center it.
            const scaleWidth = container.clientWidth / width;
            const scaleHeight = maxHeight / height;
            const scale = Math.min(scaleWidth, scaleHeight);
            const imgWidth = width * scale;
            const imgHeight = height * scale;
            this.setState({
                prevBreakpoint: currentBreakpoint,
                containerStyle: { height: `${imgHeight}px` },
                inlineStyle: { width: `${imgWidth}px`, height: imgHeight },
                width: imgWidth,
                height: imgHeight,
            });
            return;
        }

        // If we make it here, we're dealing with a small image in a big box, probably submitted by a user in the feed.
        // To make it look okay, we're just going to run it at its original size on all breakpoints, and center it. If
        // it's larger than its viewbox in smaller breakpoints, it'll be resized down by the browser.
        this.setState({
            isSmallImage: true,
            inlineStyle: {
                width: `${width}px`,
            },
            width,
            height,
        });
    };

    loadPicture = async () => {
        await this.setState({ isLazyLoad: false });
        window.removeEventListener('scroll', this.checkImageLocation);
    };

    handleOnLoad = (e) => {
        if (this.initTime === '') {
            this.initTime = Date.now();
        }
        this.setImageSize(e);
        this.setState({ isLoading: false });
    };

    render = () => {
        const {
            baseCls,
            props: { breakpoints, alt, cloudinaryID, className },
            buildSources,
            buildVideoSources,
            prioritizeSources,
            imgRef,
            vidRef,
            buildFallbackSrc,
            handleOnLoad,
            containerRef,
            state: {
                containerStyle,
                inlineStyle,
                isLoading,
                isSmallImage,
                width,
                height,
            },
        } = this;

        const breakpointKeys = Object.keys(breakpoints);

        if (!cloudinaryID) {
            return null;
        }
        return (
            <div
                className={`${baseCls} ${className} ${
                    isLoading ? `${baseCls}--loading` : ''
                } `}
                ref={containerRef}
                style={containerStyle}
            >
                {(!this.state.isLazyLoad &&
                    this.props.cloudinaryID.indexOf('anim') !== -1 && (
                        <video
                            className={`${baseCls}__picture ${baseCls}__picture--gif-video ${
                                isSmallImage ? `${baseCls}__picture--small` : ''
                            }`}
                            onPlay={handleOnLoad}
                            autoPlay
                            loop
                            muted
                            playsInline
                            ref={vidRef}
                            style={inlineStyle}
                        >
                            {flatten(
                                breakpointKeys.map(buildVideoSources)
                            ).sort(prioritizeSources)}
                        </video>
                    )) ||
                    (!this.state.isLazyLoad && (
                        <picture className={`${baseCls}__picture`}>
                            {flatten(breakpointKeys.map(buildSources)).sort(
                                prioritizeSources
                            )}
                            <img
                                className={`${baseCls}__img`}
                                src={buildFallbackSrc()}
                                onLoad={handleOnLoad}
                                ref={imgRef}
                                alt={alt}
                                style={inlineStyle}
                                width={width}
                                height={height}
                                loading="lazy"
                            />
                        </picture>
                    ))}
            </div>
        );
    };
}

ResponsivePicture.propTypes = {
    /**
     * Image Source (Cloudinary Id)
     * */
    cloudinaryID: PropTypes.string.isRequired,
    /**
     * Image Alt
     * */
    alt: PropTypes.string,
    /**
     *  Specify sizes and transforms for each breakpoint
     */
    breakpoints: PropTypes.shape({
        mobile: BREAKPOINT_SHAPE,
        smallTablet: BREAKPOINT_SHAPE,
        tablet: BREAKPOINT_SHAPE,
        desktop: BREAKPOINT_SHAPE,
    }),
    /*
     *   Max height allowed. Taller images will be shrunk and centered.
     */
    maxHeights: PropTypes.shape({
        desktop: PropTypes.number.isRequired,
        tablet: PropTypes.number.isRequired,
        smallTablet: PropTypes.number.isRequired,
        mobile: PropTypes.number.isRequired,
    }),
    /**
     * If our exact sizing needs are known (such as when an image is hard coded) we don't need to do all the mockImage stuff.
     * Setting this to true will bypass it.
     */
    forceImageSize: PropTypes.bool,

    className: PropTypes.string,
    /**
     *
     */
    useShine: PropTypes.bool,
    /**
     * Set to true to defer loading this image. It will load when it appears in the viewport, unless waitForLoadProp is set to true.
     */
    lazyLoad: PropTypes.bool,
    /**
     * Set to true to trigger load image load outside this component.
     * This will prevent ResponsivePicture from using its own logic to detect when to load when it appears in the viewport.
     */
    waitForLoadProp: PropTypes.bool,
    /**
     * Max Density controls how hi-res we're going to allow our source elements to get.
     * Generally 2x is fine but in some cases we may choose to go to 3+. This prop is here to allow for that override.
     */
    maxDensity: PropTypes.number,
};

ResponsivePicture.defaultProps = {
    alt: '',
    breakpoints: {
        mobile: {
            width: 300,
        },
        smallTablet: {
            width: 340,
        },
        tablet: {
            width: 500,
        },
    },
    className: '',
    forceImageSize: false,
    maxHeights: {
        desktop: 750,
        tablet: 750,
        smallTablet: 750,
        mobile: 750,
    },
    lazyLoad: false,
    useShine: false,
    waitForLoadProp: false,
    maxDensity: 2,
};
